import type { AnalyticsEventProperties } from '@server/analytics-events';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';

export type ChatFeedbackRating = NonNullable<AnalyticsEventProperties['chatFeedbackChanged']['rating']>;

export interface UseChatFeedbackResult {
  canRate: boolean;
  /** Eligible turns and the owner's saved selection (null rating when unset or for shared readers). */
  ratingByTurn: Map<string, ChatFeedbackRating | null>;
  eligibleTurnIds: Set<string>;
  isPending: boolean;
  pendingTurnId: string | null;
  setRating: (turnId: string, rating: ChatFeedbackRating | null) => void;
  refresh: () => void;
}

/**
 * Session-scoped feedback list + vote mutation. Query keys include workspace (via useOrpc) and
 * session so a late response from a previous session cannot update the current view.
 */
export function useChatFeedback(sessionId: string | null | undefined): UseChatFeedbackResult {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const enabled = Boolean(sessionId);
  const submissionPending = useRef(false);
  const [pendingVote, setPendingVote] = useState<{
    sessionId: string;
    turnId: string;
    orpc: typeof orpc;
  } | null>(null);

  const listOptions = orpc.chat.feedback.list.queryOptions({
    input: { sessionId: sessionId ?? '' },
  });

  const { data } = useQuery({
    ...listOptions,
    enabled,
  });

  /* eslint-disable react-hooks/refs -- mutation callbacks write the sync guard only when async handlers run, never during render */
  const mutation = useMutation(
    orpc.chat.feedback.set.mutationOptions({
      onMutate: (input) => ({
        queryKey: orpc.chat.feedback.list.queryOptions({ input: { sessionId: input.sessionId } }).queryKey,
      }),
      onSuccess: (_result, _input, context) => {
        if (!context) return;
        return queryClient.invalidateQueries({ queryKey: context.queryKey });
      },
      onSettled: () => {
        submissionPending.current = false;
        setPendingVote(null);
      },
      onError: async (error: Error, input, context) => {
        const code = 'code' in error ? error.code : undefined;
        toast.error(
          code === 'CONFLICT'
            ? 'This answer can no longer be rated'
            : code === 'NOT_FOUND'
              ? 'This chat is no longer available'
              : 'Could not save feedback. Please try again.',
        );
        if (!context) return;
        // Stop an older list response from restoring controls rejected by the server.
        await queryClient.cancelQueries({ queryKey: context.queryKey });
        if (code === 'CONFLICT' || code === 'NOT_FOUND') {
          queryClient.setQueryData<typeof data>(context.queryKey, (current) => {
            if (!current) return current;
            return code === 'NOT_FOUND'
              ? { canRate: false, turns: [] }
              : { ...current, turns: current.turns.filter((turn) => turn.turnId !== input.turnId) };
          });
        }
        return queryClient.invalidateQueries({ queryKey: context.queryKey });
      },
    }),
  );
  /* eslint-enable react-hooks/refs -- end feedback mutation block */

  const ratingByTurn = useMemo(() => {
    const map = new Map<string, ChatFeedbackRating | null>();
    for (const turn of data?.turns ?? []) {
      map.set(turn.turnId, turn.rating);
    }
    return map;
  }, [data?.turns]);

  const eligibleTurnIds = useMemo(() => new Set(ratingByTurn.keys()), [ratingByTurn]);

  const setRating = useCallback(
    (turnId: string, rating: ChatFeedbackRating | null) => {
      if (!sessionId || !data?.canRate || submissionPending.current || mutation.isPending) return;
      submissionPending.current = true;
      setPendingVote({ sessionId, turnId, orpc });
      mutation.mutate({ sessionId, turnId, rating });
    },
    [sessionId, data?.canRate, mutation, orpc],
  );

  const refresh = useCallback(() => {
    if (!sessionId) return;
    const queryKey = orpc.chat.feedback.list.queryOptions({ input: { sessionId } }).queryKey;
    void queryClient.invalidateQueries({ queryKey });
  }, [sessionId, orpc, queryClient]);

  return {
    canRate: data?.canRate ?? false,
    ratingByTurn,
    eligibleTurnIds,
    isPending: pendingVote !== null,
    pendingTurnId:
      pendingVote && pendingVote.sessionId === sessionId && pendingVote.orpc === orpc ? pendingVote.turnId : null,
    setRating,
    refresh,
  };
}
